'use client';

import getImages from '@/actions/services/getImages';
import ArrowRight from '@/assets/svg/ArrowRight';
import { useWindowWidth } from '@/lib/useWindowWidth';
import { StrapiImage } from '@/types/img';
import { Service } from '@/types/service';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { BsVolumeMuteFill, BsVolumeUpFill } from 'react-icons/bs';
import { date } from 'zod';

interface Props {
  service: Service;
  button: string;
  locale: string;
  slug: string;
}

const largeSizes = [
  {
    w: 1600,
    h: 911,
  },
  {
    w: 794,
    h: 591,
  },
  {
    w: 786,
    h: 414,
  },
];

const smallSizes = [
  {
    w: 1072,
    h: 610,
  },
  {
    w: 531,
    h: 395,
  },
  {
    w: 526,
    h: 277,
  },
];

export default function ImagesSectionShort({
  service,
  button,
  locale,
  slug,
}: Props) {
  const videoRefFirst = useRef<HTMLVideoElement>(null);
  const [isMutedFirst, setIsMutedFirst] = useState(true);

  const toggleMuteFirst = () => {
    if (videoRefFirst.current) {
      videoRefFirst.current.muted = !videoRefFirst.current.muted;
      setIsMutedFirst(videoRefFirst.current.muted);
    }
  };
  const videoRefSecond = useRef<HTMLVideoElement>(null);
  const [isMutedSecond, setIsMutedSecond] = useState(true);

  const toggleMuteSecond = () => {
    if (videoRefSecond.current) {
      videoRefSecond.current.muted = !videoRefSecond.current.muted;
      setIsMutedSecond(videoRefSecond.current.muted);
    }
  };
  const videoRefThird = useRef<HTMLVideoElement>(null);
  const [isMutedThird, setIsMutedThird] = useState(true);

  const toggleMuteThird = () => {
    if (videoRefThird.current) {
      videoRefThird.current.muted = !videoRefThird.current.muted;
      setIsMutedThird(videoRefThird.current.muted);
    }
  };

  const [images, setImages] = useState<
    {
      id: number;
      indexId: number;
      img: StrapiImage;
      media: 'image' | 'video';
    }[]
  >([]);

  const width = useWindowWidth();

  useEffect(() => {
    getImages({
      locale: locale,
      slug,
      baseUrl: true,
    }).then((res) => {
      if (res.data?.data) {
        setImages(
          res.data.data[0].images.sort((a, b) => a.indexId - b.indexId)
        );
      }
    });
  }, [service]);

  const sizes = width <= 1650 ? smallSizes : largeSizes;

  return (
    <div className='px-28 sm:px-0 2xl:pl-[160px] 2xl:pr-[160px] pr-[90px] pl-[90px]'>
      <div className='flex text-black sm:px-0 sm:pb-10 sm:pt-0 py-20 justify-between gap-12'>
        <div className='flex flex-col gap-16 sm:gap-8'>
          <div className='grid grid-cols-3 sm:grid-cols-1 sm:px-4 gap-24 sm:items-start sm:gap-4 items-start w-full'>
            <h2 className='2xl:text-[55px] text-[36px] col-span-2 sm:col-span-1 sm:text-[24px] leading-[110%] text-[#0D1524] sm:leading-[110%] sm:w-full font-normal sm:mt-0'>
              {service.title}
            </h2>
            <p className='2xl:text-[18px] text-[13px] sm:text-[15px] sm:leading-[110%] mt-2 text-[#0D1524] leading-[113%]'>
              {service.shortDesc}
            </p>
          </div>
          <div className='sm:grid hidden  '>
            {images.slice(0, 1).map((i) => (
              <>
                {i?.media === 'video' ? (
                  <video
                    src={
                      typeof i.img?.url === 'string'
                        ? i.img.url
                        : (i.img?.url as { src?: string })?.src ?? ''
                    }
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload='auto'
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                  />
                ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                  <Image
                    src={i.img.url}
                    width={i.img.width}
                    height={i.img.height}
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                    alt=''
                    unoptimized
                  />
                ) : null}
              </>
            ))}
          </div>
          <div className='sm:grid hidden px-4 mb-[10px] '>
            <p className='text-[15px] leading-[120%] uppercase font-bold  mb-[21px]'>
              About the service
            </p>
            <p className='font-poppins text-[15px] '>
              {service.about_services}
            </p>
          </div>

          <div className='sm:px-4 flex justify-between gap-10 flex-wrap sm:flex-col'>
            <div className='sm:grid hidden gap-4'>
              {images.slice(1).map((i) => (
                <>
                  {i?.media === 'video' ? (
                    <video
                    src={
                      typeof i.img?.url === 'string'
                        ? i.img.url
                        : (i.img?.url as { src?: string })?.src ?? ''
                    }
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload='auto'
                      className='object-cover rounded-[20px] '
                    />
                  ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                    <Image
                      src={i.img.url}
                      width={i?.img.width}
                      height={i?.img.height}
                      className='object-cover rounded-[20px]'
                      alt=''
                      unoptimized
                    />
                  ) : null}
                </>
              ))}
            </div>
            {/* Benefits */}

            <div className='max-w-[400px]'>
              <p className='2xl:text-[30px] text-[20px] sm:text-[15px] leading-[120%] uppercase font-bold mb-6'>
                Benefits
              </p>
              <div className='flex flex-col gap-2'>
                {service.Benefits?.map((i) => (
                  <div
                    key={i.id}
                    className='border rounded-[80px] w-max border-[#0D1524] px-4 py-1 2xl:text-[20px] text-[13px] sm:text-[13px] leading-[120%] uppercase'
                  >
                    {i.name}
                  </div>
                ))}
              </div>
            </div>

            {/* About the Service */}
            <div className='2xl:max-w-[773px] w-[517px] sm:hidden '>
              <p className='2xl:text-[30px] text-[20px] sm:text-[15px] leading-[120%] uppercase font-bold mb-[14px]'>
                About the service
              </p>
              <p className='font-poppins 2xl:text-[20px] text-[13px] sm:text-[14px]'>
                {service.about_services}
              </p>
            </div>
          </div>

          <div className='sm:grid hidden'>
            {images.slice(2, images.length - 1).map((i) => (
              <>
                {i?.media === 'video' ? (
                  <video
                    src={
                      typeof i.img?.url === 'string'
                        ? i.img.url
                        : (i.img?.url as { src?: string })?.src ?? ''
                    }
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload='auto'
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                  />
                ) : i?.img?.url && i?.img?.width && i?.img?.height ? (
                  <Image
                    src={i.img.url}
                    width={i.img.width}
                    height={i.img.height}
                    className='masonry-item-service-mobile object-cover rounded-[20px]'
                    alt=''
                    unoptimized
                  />
                ) : null}
              </>
            ))}
          </div>
        </div>
      </div>
      <div className='flex justify-center w-full h-full'>
        <section className='w-full  sm:hidden masonry-gallery-service flex flex-col gap-6'>
          <div className='w-full rounded-[20px] overflow-hidden'>
            {images[0]?.media === 'video' ? (
              <div className='relative'>
                <video
                  src={
                    typeof images[0].img?.url === 'string'
                      ? images[0].img.url
                      : (images[0].img?.url as { src?: string })?.src ?? ''
                  }
                  ref={videoRefFirst}
                  onContextMenu={(e) => e.preventDefault()}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload='auto'
                  className='w-full h-full object-cover rounded-[20px]'
                />
                <button
                  onClick={toggleMuteFirst}
                  className='absolute bottom-4 right-4 bg-black/50 text-white p-2 rounded-full'
                >
                  {isMutedFirst ? <BsVolumeMuteFill /> : <BsVolumeUpFill />}
                </button>
              </div>
            ) : images[0]?.img?.url &&
              images[0]?.img?.width &&
              images[0]?.img?.height ? (
              <Image
                src={images[0].img.url}
                width={sizes[0].w}
                height={sizes[0].h}
                className='w-full h-full object-cover rounded-[20px]'
                alt=''
                onContextMenu={(e) => e.preventDefault()}
                unoptimized
              />
            ) : null}
          </div>

          <div className='flex gap-6'>
            <div className='w-1/2 rounded-[20px] overflow-hidden'>
              {images[1]?.media === 'video' ? (
                <div className='relative'>
                  <video
                    src={
                      typeof images[1].img?.url === 'string'
                        ? images[1].img.url
                        : (images[1].img?.url as { src?: string })?.src ?? ''
                    }
                    autoPlay
                    loop
                    ref={videoRefSecond}
                    onContextMenu={(e) => e.preventDefault()}
                    muted
                    playsInline
                    preload='auto'
                    className='w-full h-full object-cover rounded-[20px]'
                  />
                  <button
                    onClick={toggleMuteSecond}
                    className='absolute bottom-4 right-4 bg-black/50 text-white p-2 rounded-full'
                  >
                    {isMutedSecond ? <BsVolumeMuteFill /> : <BsVolumeUpFill />}
                  </button>
                </div>
              ) : images[1]?.img?.url &&
                images[1]?.img?.width &&
                images[1]?.img?.height ? (
                <Image
                  src={images[1].img.url}
                  width={sizes[1].w}
                  height={sizes[1].h}
                  className='w-full h-full object-cover rounded-[20px]'
                  alt=''
                  onContextMenu={(e) => e.preventDefault()}
                  unoptimized
                />
              ) : null}
            </div>
            {/* Правая колонка — колонка из 2-х блоков */}
            <div className='w-1/2 flex flex-col gap-6'>
              <div className='rounded-[20px] overflow-hidden'>
                {images[2]?.media === 'video' ? (
                  <div className='relative'>
                    <video
                      src={
                      typeof images[2].img?.url === 'string'
                        ? images[2].img.url
                        : (images[2].img?.url as { src?: string })?.src ?? ''
                      }
                      autoPlay
                      loop
                      ref={videoRefThird}
                      onContextMenu={(e) => e.preventDefault()}
                      muted
                      playsInline
                      width={sizes[2].w}
                      height={sizes[2].h}
                      preload='auto'
                      className='w-full h-full object-cover rounded-[20px]'
                    />
                    <button
                      onClick={toggleMuteThird}
                      className='absolute bottom-4 right-4 bg-black/50 text-white p-2 rounded-full'
                    >
                      {isMutedThird ? <BsVolumeMuteFill /> : <BsVolumeUpFill />}
                    </button>
                  </div>
                ) : images[2]?.img?.url &&
                  images[2]?.img?.width &&
                  images[2]?.img?.height ? (
                  <Image
                    src={images[2].img.url}
                    width={sizes[2].w}
                    height={sizes[2].h}
                    className='w-full h-full object-cover'
                    alt=''
                    onContextMenu={(e) => e.preventDefault()}
                    unoptimized
                  />
                ) : null}
              </div>
              <div className='flex justify-center items-center  p-6 rounded-[20px]  py-12'>
                <Link href='/contact' className='inline-block w-fit'>
                  <div className='flex gap-6 items-center'>
                    <div className='hover:underline 2xl:text-[72px] text-[48px] text-black leading-none tracking-[-0.05em]'>
                      {button}
                    </div>
                    <div className='mt-2 2xl:w-[49px] 2xl:h-[40px] w-[32px] h-[26px]'>
                      <ArrowRight className='fill-black' />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
